import { defineStore } from 'pinia'
import { computed, type ComputedRef, type Ref, ref } from 'vue'

import { normalizeQuoteLineNumbers } from '@/components/quote/composables/business/quoteLineNormalizer'
import { DataServiceFactory, EntraIDAuthService, QuoteService } from '@/factories'
import type {
	IClient,
	IQuote,
	IQuoteDocumentTextLine,
	IQuoteIndex,
	IQuoteLine,
	IQuoteSearchParams,
	IQuoteState,
	IQuoteStateTransition,
	IQuoteTextLine,
	IStoreEntity,
	ISubJob,
	ISubJobSearchResult
} from '@/interfaces'
import { Quote, QuoteDocumentTextLine, QuoteLine, QuoteTextLine } from '@/models'
import { Collection } from '@/models/Collection'
import { EntraIDAuthConfig } from '@/services'
import { AccessLevel, type IFullStore, useAuthStore, useBaseStore } from '@/stores'

// Phase 1: Create IQuoteStore Interface
export interface IQuoteStore<T extends IStoreEntity, TI = T> extends IFullStore<T, TI> {
	// Quote-specific state
	quoteStates: Ref<IQuoteState[]>
	quoteStateTransitions: Ref<IQuoteStateTransition[]>
	pendingApprovalQuotes: Ref<IQuote[]>
	recentQuotes: Ref<IQuote[]>
	pendingSubjobId: Ref<string | number | null>
	currentSubJob: Ref<ISubJob | null>

	// Quote-specific computed
	quotesBySubJob: ComputedRef<(subjobId: number) => IQuoteIndex[]>
	openQuotes: ComputedRef<IQuote[]>
	closedQuotes: ComputedRef<IQuote[]>

	// Quote-specific methods
	addNew: () => IQuote
	fetchQuotesBySubJob: (subjobId: number, forceRefresh?: boolean) => Promise<IQuoteIndex[]>
	clearQuoteIndicesCache: (subjobId?: number) => void
	createQuote: (quoteData: Partial<IQuote>) => Promise<IQuote>
	updateQuote: (quote: IQuote) => Promise<IQuote>
	deleteQuote: (quoteId: number | string) => Promise<void>
	closeQuote: (quoteId: number | string) => Promise<void>
	cancelQuote: (quoteId: number | string) => Promise<void>
	transitionQuoteState: (quoteId: number | string, newState: number, notes?: string) => Promise<IQuote>
	getQuoteStates: () => Promise<IQuoteState[]>
	getQuoteStateTransitions: () => Promise<IQuoteStateTransition[]>
	loadRelatedData: (subjobId?: string | number) => Promise<void>
	searchSubJobs: (query: string) => Promise<ISubJobSearchResult[]>
	createNewQuoteForSubJob: (subjobId: string | number) => Promise<IQuote>
	canEditQuote: (quote: IQuote) => boolean
	canTransitionQuote: (quote: IQuote, targetState: number) => boolean
	calculateQuoteTotal: (quote: IQuote) => number
	searchQuotes: (params?: IQuoteSearchParams) => Promise<IQuoteIndex[]>
	duplicateQuote: (sourceQuoteId: string | number) => Promise<IQuote>
}

const authService = DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as EntraIDAuthService
const quoteService = DataServiceFactory.createService('Quote', authService) as QuoteService

// Phase 2: Refactor Quote Store Structure
export const useQuoteStore = defineStore<'quote-store', IQuoteStore<IQuote, IQuote>>('quote-store', () => {
	// Temporary storage for subjobId when creating new quotes
	const pendingSubjobId = ref<string | number | null>(null)

	// Destructure all baseStore properties including column filter methods
	const {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		viewList,
		filterList,
		pageSize,
		// Column filters for PrimeTable integration
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,

		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		canRead,
		canAdd,
		clearActive,
		clearSearch,
		calcHash,

		// Column filter methods from baseStore
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter
	} = useBaseStore<IQuote, 'Quote', IQuote>('Quote')

	// Add quote-specific state
	const quoteStates = ref<IQuoteState[]>([])
	const quoteStateTransitions = ref<IQuoteStateTransition[]>([])
	const pendingApprovalQuotes = ref<IQuote[]>([])
	const recentQuotes = ref<IQuote[]>([])

	// Cache for quote indices by subjob
	const quoteIndicesBySubjob = ref<Map<number, IQuoteIndex[]>>(new Map())

	// Store the current SubJob for permission checking
	const currentSubJob = ref<ISubJob | null>(null)

	// Legacy state for backward compatibility (will be removed later)
	const isLoading = ref(false)
	const errors = ref<string[]>([])

	// Phase 3: Create Adapter Methods for backward compatibility
	const currentQuote = computed({
		get: () => activeRecord.value,
		set: (value) => {
			activeRecord.value = value
		}
	})

	const quotes = computed({
		get: () => records.value,
		set: (value) => {
			records.value = value
		}
	})

	// Map fetchQuote to get
	async function fetchQuote(quoteId: number | string): Promise<IQuote> {
		return get(quoteId)
	}

	// Implement getIndex for quotes
	async function getIndex(): Promise<IQuote[]> {
		try {
			// TODO: Implement getAllQuotes endpoint or use pagination
			records.value = []
			return records.value
		} catch (error) {
			console.error('Failed to fetch quotes:', error)
			throw error
		}
	}

	// Implement get for single quote - loads quote with all related data
	async function get(quoteId: string | number, subjobId?: string | number): Promise<IQuote> {
		storeActive.value = true
		try {
			// Handle new quote creation
			if (quoteId === '0' || quoteId === 0) {
				const newQuote = addNew()

				// Use provided subjobId or check for pending subjobId (from QuoteEdit component)
				const targetSubjobId = subjobId || pendingSubjobId.value
				if (targetSubjobId) {
					newQuote.subjobId = typeof targetSubjobId === 'string' ? parseInt(targetSubjobId) : targetSubjobId
					// Clear pending subjobId after use
					pendingSubjobId.value = null

					// Load related data for the new quote with subjobId
					activeRecord.value = newQuote
					activeRecordSavedHash.value = calcHash(newQuote)

					// Temporarily set storeActive to false to allow loadRelatedData to work
					storeActive.value = false
					await loadRelatedData(targetSubjobId)
					storeActive.value = true

					return activeRecord.value // Return the updated record after loading related data
				}

				activeRecord.value = newQuote
				activeRecordSavedHash.value = calcHash(newQuote)
				return newQuote
			}

			// Fetch existing quote
			const quote = await quoteService.getQuote(quoteId)
			activeRecord.value = quote
			activeRecordSavedHash.value = calcHash(quote)

			// Update in records list if exists
			const index = records.value.findIndex((q) => q.quoteId === quoteId)
			if (index >= 0) {
				records.value[index] = quote
			} else {
				records.value.push(quote)
			}

			// Automatically load related data for existing quotes
			// This ensures subjob is loaded even on direct URL navigation
			if (quote.subjobId || quote.subjobNumber) {
				await loadRelatedData()
			}

			return quote
		} catch (error) {
			console.error('Failed to fetch quote:', error)
			activeRecord.value = {} as IQuote
			throw error
		} finally {
			storeActive.value = false
		}
	}

	// Load related data for the active quote
	async function loadRelatedData(subjobId?: string | number): Promise<void> {
		// For new quotes, use the provided subjobId; for existing quotes, use the quote's subjobId or subjobNumber
		let targetSubjobId: string | number | undefined = subjobId || activeRecord.value?.subjobId

		// If we don't have a subjobId but have a subjobNumber, use that instead
		if ((!targetSubjobId || targetSubjobId === 0) && activeRecord.value?.subjobNumber) {
			targetSubjobId = activeRecord.value.subjobNumber
		}

		console.log('Loading related data for quote:', { targetSubjobId })

		if (!activeRecord.value) return

		storeActive.value = true
		try {
			const promises: Promise<IQuoteState[] | IQuoteStateTransition[] | IQuote | ISubJob | IClient | void>[] = []

			// Load reference data if needed
			if (!quoteStates.value.length) {
				promises.push(getQuoteStates())
			}
			if (!quoteStateTransitions.value.length) {
				promises.push(getQuoteStateTransitions())
			}

			// Load SubJob (critical for both new and existing quotes)
			if (targetSubjobId && targetSubjobId !== 0) {
				const { useSubJobStore } = await import('./subJobStore')
				const subJobStore = useSubJobStore()
				await subJobStore.get(String(targetSubjobId))

				// After subjob loads, populate quote fields for new quotes
				if (subJobStore.activeRecord) {
					// Set currentSubJob for permission checking (critical for sales team permissions)
					currentSubJob.value = subJobStore.activeRecord

					// SubJob is now available for permission checking directly from subJobStore
					// Populate quote fields for new quotes
					if (activeRecord.value && (activeRecord.value.id === 0 || activeRecord.value.id === '0')) {
						// Populate quote fields from subjob
						activeRecord.value.subjobId =
							typeof subJobStore.activeRecord.id === 'string'
								? parseInt(subJobStore.activeRecord.id)
								: (subJobStore.activeRecord.id as number)
						activeRecord.value.subjobNumber = subJobStore.activeRecord.subjobNumber || ''
						activeRecord.value.location = String(subJobStore.activeRecord.location || '')
						// The component will handle clientName, clientCode from job
					}
				}
			}

			// Load Client
			if (activeRecord.value.clientCode) {
				// Existing quote with clientCode
				const { useClientStore } = await import('./clientStore')
				const clientStore = useClientStore()
				promises.push(clientStore.get(activeRecord.value.clientCode))
			} else if (targetSubjobId && targetSubjobId !== 0) {
				// New quote - load client by subjobId
				const { useClientStore } = await import('./clientStore')
				const clientStore = useClientStore()
				await clientStore.getBySubJobId(targetSubjobId)

				// After client loads, populate quote fields for new quotes
				if (activeRecord.value.id === 0 || activeRecord.value.id === '0') {
					if (clientStore.activeRecord && activeRecord.value) {
						// Populate client fields from loaded client
						activeRecord.value.clientCode = String(clientStore.activeRecord.id || '')
						activeRecord.value.clientName = clientStore.activeRecord.name || ''
					}
				}
			}

			// Wait for all data to load
			await Promise.all(promises)

			// Recalculate hash after all related data is loaded
			// Skip hash recalculation for duplicate quotes to preserve dirty state
			if (activeRecord.value && !isDuplicating.value) {
				activeRecordSavedHash.value = calcHash(activeRecord.value)
			}
		} catch (error) {
			console.error('Failed to load related data:', error)
		} finally {
			storeActive.value = false
		}
	}

	// TODO: Implement Loader using the storeActive flag in BaseStore, loaderOverride() in BaseForm.
	// See SubjobEdit loadParentData() line 373
	// Get quote with all related data loaded - DEPRECATED: Use get() followed by loadRelatedData()
	async function getQuoteWithRelatedData(quoteId: string | number): Promise<IQuote> {
		// Handle new quote creation case
		if (quoteId === '0' || quoteId === 0) {
			const newQuote = addNew()
			activeRecord.value = newQuote
			// Load reference data for new quotes
			await Promise.all([getQuoteStates(), getQuoteStateTransitions()])
			return newQuote
		}

		// First load the quote
		let quote: IQuote
		try {
			quote = await quoteService.getQuote(quoteId)
			activeRecord.value = quote
			activeRecordSavedHash.value = calcHash(quote)

			// Update in records list if exists
			const index = records.value.findIndex((q) => q.quoteId === quoteId)
			if (index >= 0) {
				records.value[index] = quote
			} else {
				records.value.push(quote)
			}
		} catch (error) {
			console.error('Failed to fetch quote:', error)
			activeRecord.value = {} as IQuote
			throw error
		}

		// Load reference data in parallel
		const referencePromises: Promise<IQuoteState[] | IQuoteStateTransition[]>[] = []

		// Load reference data if not already loaded
		if (!quoteStates.value.length) {
			referencePromises.push(getQuoteStates())
		}
		if (!quoteStateTransitions.value.length) {
			referencePromises.push(getQuoteStateTransitions())
		}

		// Start loading reference data
		const referenceDataPromise = Promise.all(referencePromises)

		// Chain load SubJob after quote is loaded (since we need subjobId from quote)
		const subJobPromise = Promise.resolve()
		if (quote.subjobId && quote.subjobId !== 0) {
			const { useSubJobStore } = await import('./subJobStore')
			const subJobStore = useSubJobStore()
			await subJobStore.get(String(quote.subjobId))
			// Set currentSubJob for permission checking (critical for sales team permissions)
			if (subJobStore.activeRecord) {
				currentSubJob.value = subJobStore.activeRecord
			}
		} else if (quote.subjobNumber) {
			// If subjobId is 0, fall back to using subjobNumber
			const { useSubJobStore } = await import('./subJobStore')
			const subJobStore = useSubJobStore()
			await subJobStore.get(quote.subjobNumber)
			// Set currentSubJob for permission checking (critical for sales team permissions)
			if (subJobStore.activeRecord) {
				currentSubJob.value = subJobStore.activeRecord
			}
		}

		// Load client data (can be parallel since we have clientCode from quote)
		const clientPromise = Promise.resolve()
		if (quote.clientCode) {
			const { useClientStore } = await import('./clientStore')
			const clientStore = useClientStore()
			await clientStore.get(quote.clientCode)
		}

		// Wait for all data to load
		await Promise.all([referenceDataPromise, subJobPromise, clientPromise])

		return quote
	}

	// Implement unified save method
	async function save(quote: IQuote): Promise<IQuote> {
		// Ensure totals are recalculated from line items before saving
		// This fixes issues where line edits don't trigger updateTotal()
		if (quote instanceof Quote && typeof quote.updateTotal === 'function') {
			quote.updateTotal()
		}

		if (!quote.id || quote.id === 0 || quote.id === '0' || isDuplicating.value === true) {
			// Store original values that API might not return
			const originalSubjobNumber = quote.subjobNumber
			const originalSubjobId = quote.subjobId

			// Check if we're on the duplicate route - if so, use createQuote endpoint

			// Normalize quote line numbers before sending to API
			// This ensures lineNum and afterLineNumber references are correct
			const normalizedQuote = normalizeQuoteLineNumbers(quote)

			// Create new quote - use promise chain like subJobStore
			return quoteService
				.createQuote(normalizedQuote)
				.then((data) => {
					// data is already a Quote instance from the service
					// Preserve original fields if API didn't return them
					if (!data.subjobNumber && originalSubjobNumber) {
						data.subjobNumber = originalSubjobNumber
					}
					if (!data.subjobId && originalSubjobId) {
						data.subjobId = originalSubjobId
					}

					records.value.push(data)
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)

					// Clear cache for this subjob
					if (data.subjobId) {
						clearQuoteIndicesCache(data.subjobId)
					}

					return activeRecord.value // Return activeRecord.value
				})
				.catch((err) => {
					console.error('Failed to create quote:', {
						error: err,
						response: err?.response,
						data: err?.response?.data,
						quote: quote
					})
					throw err
				})
		} else {
			// Normalize quote line numbers before sending to API
			// This ensures lineNum and afterLineNumber references are correct
			const normalizedQuote = normalizeQuoteLineNumbers(quote)

			// Update existing quote - use promise chain
			return quoteService
				.updateQuote(normalizedQuote)
				.then((data) => {
					// data is already a Quote instance from the service
					const index = records.value.findIndex((q) => q.quoteId === quote.quoteId)
					if (index >= 0) {
						records.value[index] = data
					}
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)

					// Clear cache for this subjob
					if (data.subjobId) {
						clearQuoteIndicesCache(data.subjobId)
					}

					return activeRecord.value // Return activeRecord.value
				})
				.catch((err) => {
					console.error('Failed to update quote:', {
						error: err,
						response: err?.response,
						data: err?.response?.data,
						quoteId: quote.quoteId
					})
					throw err
				})
		}
	}

	// Computed getters for convenience
	const quotesBySubJob = computed(() => (subjobId: number) => {
		// First check the indices cache
		const indices = quoteIndicesBySubjob.value.get(subjobId)
		if (indices) {
			return indices
		}

		// No fallback - QuoteIndex objects should only come from the API endpoint
		// The IQuote and IQuoteIndex interfaces have different fields and cannot be mapped reliably
		return []
	})

	const openQuotes = computed(() => records.value.filter((q) => q.quoteStatus === 'Open'))

	const closedQuotes = computed(() => records.value.filter((q) => q.quoteStatus === 'Closed'))

	// Implement addNew method
	function addNew(): IQuote {
		const newQuote = new Quote({
			id: 0,
			quoteId: 0,
			subjobId: 0,
			jobNumber: '',
			clientCode: '',
			clientName: '',
			clientContactId: '',
			clientContactName: '',
			location: '',
			quoteDate: new Date(),
			docNumber: '',
			revision: '1.0',
			headerComments: '',
			footerComments: '',
			estimatorNotes: '',
			submissionNotes: '',
			quoteState: 0,
			quoteStateAlias: 'New',
			quoteStatus: 'Open',
			quoteTotal: 0,
			workOrderId: undefined,
			createDate: new Date(),
			// createBy: API will handle this automatically
			// createByName: API will handle this automatically
			lastUpdateDate: new Date(),
			// lastUpdateBy: API will handle this automatically
			quoteLines: new Collection(QuoteLine, []),
			jobType: 'QUOTE',
			isCostJobType: false,
			approvalRequired: false,
			approvedBy: undefined,
			approvedDate: undefined
		})

		activeRecord.value = newQuote
		activeRecordSavedHash.value = calcHash(newQuote)
		return newQuote
	}

	async function fetchQuotesBySubJob(subjobId: number, forceRefresh: boolean = true): Promise<IQuoteIndex[]> {
		try {
			// Check cache first unless force refresh is requested
			if (!forceRefresh) {
				const cached = quoteIndicesBySubjob.value.get(subjobId)
				if (cached && cached.length > 0) {
					return cached
				}
			}

			// Fetch QuoteIndex models from service
			const fetchedQuotes = await quoteService.getQuotesBySubJob(subjobId)

			// Cache the results
			quoteIndicesBySubjob.value.set(subjobId, fetchedQuotes)

			return fetchedQuotes
		} catch (error) {
			console.error('Failed to fetch quotes by subjob:', error)
			throw error
		}
	}

	async function createQuote(quoteData: Partial<IQuote>): Promise<IQuote> {
		try {
			// Use the save method for consistency
			return await save(quoteData as IQuote)
		} catch (error) {
			console.error('Failed to create quote:', error)
			throw error
		}
	}

	async function updateQuote(quote: IQuote): Promise<IQuote> {
		try {
			// Use the save method for consistency
			return await save(quote)
		} catch (error) {
			console.error('Failed to update quote:', error)
			throw error
		}
	}

	async function deleteQuote(quoteId: number | string): Promise<void> {
		try {
			await quoteService.deleteQuote(quoteId)
			const index = records.value.findIndex((q) => q.quoteId === quoteId)
			if (index >= 0) {
				records.value.splice(index, 1)
			}
			if (activeRecord.value?.quoteId === quoteId) {
				activeRecord.value = null
			}
		} catch (error) {
			console.error('Failed to delete quote:', error)
			throw error
		}
	}

	/**
	 * Close a quote permanently
	 * @param quoteId - The ID of the quote to close
	 */
	async function closeQuote(quoteId: number | string): Promise<void> {
		try {
			// Call the close endpoint
			await quoteService.closeQuote(quoteId)
			// Fetch the updated quote to reflect the new status
			const updatedQuote = await quoteService.getQuote(quoteId)
			// Update the active record if it's the current quote
			if (activeRecord.value?.quoteId === quoteId) {
				activeRecord.value = updatedQuote
			}
			// Update in the records array
			const index = records.value.findIndex((q) => q.quoteId === quoteId)
			if (index >= 0) {
				records.value[index] = updatedQuote
			}
		} catch (error) {
			console.error('Failed to close quote:', error)
			throw error
		}
	}

	/**
	 * Cancel a quote permanently (soft delete)
	 * @param quoteId - The ID of the quote to cancel
	 */
	async function cancelQuote(quoteId: number | string): Promise<void> {
		try {
			// Call the cancel endpoint
			await quoteService.cancelQuote(quoteId)
			// Fetch the updated quote to reflect the new status
			const updatedQuote = await quoteService.getQuote(quoteId)
			// Update the active record if it's the current quote
			if (activeRecord.value?.quoteId === quoteId) {
				activeRecord.value = updatedQuote
			}
			// Update in the records array
			const index = records.value.findIndex((q) => q.quoteId === quoteId)
			if (index >= 0) {
				records.value[index] = updatedQuote
			}
		} catch (error) {
			console.error('Failed to cancel quote:', error)
			throw error
		}
	}

	async function transitionQuoteState(quoteId: number | string, toState: number): Promise<IQuote> {
		// Use the baseStore loading state
		// loading.value = true
		// error.value = null

		try {
			const response = await quoteService.transitionState(quoteId, toState)

			// Update current quote if it matches
			if (activeRecord.value && activeRecord.value.quoteId === quoteId) {
				activeRecord.value.quoteState = response.quoteState
				activeRecord.value.quoteStateAlias = response.quoteStateAlias
				activeRecordSavedHash.value = calcHash(activeRecord.value)
			}

			// Update quote in list if present
			const index = records.value.findIndex((q) => q.quoteId === quoteId)
			if (index !== -1) {
				records.value[index].quoteState = response.quoteState
				records.value[index].quoteStateAlias = response.quoteStateAlias
			}

			// Show success message
			// const stateObj = quoteStates.value.find((s) => (s.id || s.stateId) === toState)
			// window.$message.success(`Quote transitioned to ${stateObj?.name || stateObj?.stateName || `State ${toState}`}`)

			return response
		} finally {
			// loading.value = false
		}
	}

	async function getQuoteStates(): Promise<IQuoteState[]> {
		try {
			if (quoteStates.value.length === 0) {
				quoteStates.value = await quoteService.getQuoteStates()
			}
			return quoteStates.value
		} catch (error) {
			console.error('Failed to fetch quote states:', error)
			throw error
		}
	}

	async function getQuoteStateTransitions(): Promise<IQuoteStateTransition[]> {
		try {
			if (quoteStateTransitions.value.length === 0) {
				quoteStateTransitions.value = await quoteService.getQuoteStateTransitions()
			}
			return quoteStateTransitions.value
		} catch (error) {
			console.error('Failed to fetch quote state transitions:', error)
			throw error
		}
	}

	// Helper method to get available transitions by current state
	function getAvailableTransitionsByState(currentState: number): IQuoteState[] {
		// Get transitions from the current state
		const availableTransitions = quoteStateTransitions.value.filter((t) => t.fromState === currentState)

		// Map transitions to their target states
		const targetStateIds = availableTransitions.map((t) => t.toState)

		// Return the actual state objects for the target states
		return quoteStates.value.filter((state) => {
			const stateId = state.id || state.stateId
			return stateId != null && targetStateIds.includes(stateId)
		})
	}

	// Business logic functions
	function canEditQuote(quote: IQuote): boolean {
		return quote.quoteStatus === 'Open' && quote.quoteState <= 3
	}

	function canTransitionQuote(quote: IQuote): boolean {
		// Transition logic will be handled by backend/common store
		// For now, allow all transitions and let the backend validate
		return quote.quoteStatus !== 'Closed'
	}

	function calculateQuoteTotal(quote: IQuote): number {
		return quote.quoteLines.reduce((total, line) => {
			if (!line.isDeleted && !line.isAlternative) {
				return total + (line.price || 0)
			}
			return total
		}, 0)
	}

	// Dashboard support: Search quotes with filtering
	async function searchQuotes(params: IQuoteSearchParams = {}): Promise<IQuoteIndex[]> {
		try {
			const quotes = await quoteService.searchQuotes(params)
			// Store the results for potential use in other components
			// Note: These are IQuoteIndex models, not full IQuote models
			// We don't update the main records array here since it expects IQuote type
			return quotes
		} catch (error) {
			console.error('Failed to search quotes:', error)
			throw error
		}
	}

	// Duplicate quote functionality
	async function duplicateQuote(sourceQuoteId: string | number): Promise<IQuote> {
		storeActive.value = true
		try {
			// First, get the source quote with all its data
			const sourceQuote = await quoteService.getQuote(sourceQuoteId)

			// Create a new quote based on the source, but with reset fields
			const duplicatedQuote = new Quote({
				// Reset identification fields
				id: 0,
				quoteId: 0,

				// Clear subjob and client fields per requirements
				subjobId: 0,
				subjobNumber: '',
				clientCode: '',
				clientName: '',

				// Copy structural fields
				location: sourceQuote.location,
				revision: '1.0', // Reset revision
				headerComments: sourceQuote.headerComments,
				footerComments: sourceQuote.footerComments,
				estimatorNotes: sourceQuote.estimatorNotes,
				submissionNotes: sourceQuote.submissionNotes,

				// Reset state to draft
				quoteState: 0,
				quoteStateAlias: 'New',
				quoteStatus: 'Open',

				// Reset financial fields to 0
				quoteTotal: 0,
				totalTax: 0,
				totalCost: 0,

				// Set dates to current
				quoteDate: new Date(),
				createDate: new Date(),
				lastUpdateDate: new Date(),
				updateDate: new Date(),

				// Copy job classification
				jobType: sourceQuote.jobType || 'QUOTE',
				isCostJobType: sourceQuote.isCostJobType || false,

				// Reset approval fields
				approvalRequired: false,
				approvedBy: undefined,
				approvedDate: undefined,

				// Copy estimate method
				estimateMethod: sourceQuote.estimateMethod,

				// Copy people fields
				salespersonId: sourceQuote.salespersonId,
				salespersonName: sourceQuote.salespersonName,
				salesTeamName: sourceQuote.salesTeamName,

				// Copy subjob dates
				kickDate: sourceQuote.kickDate,
				shipDate: sourceQuote.shipDate,

				// Reset SCR status
				scrStatus: undefined,
				scrPending: false,

				// Duplicate quote lines (excluding sales tax)
				quoteLines: new Collection(
					QuoteLine,
					sourceQuote.quoteLines
						?.filter((line: IQuoteLine) => {
							// Exclude sales tax lines as per requirements
							return !(line.isTax || (line.itemCode && line.itemCode.toLowerCase() === 'salestax'))
						})
						.map((line: IQuoteLine) => {
							// Create new line with reset financial fields
							return new QuoteLine({
								lineId: 0, // Reset line ID for new quote
								quoteId: 0, // Will be set when quote is saved
								lineNum: line.lineNum,
								item: line.item,
								itemCode: line.itemCode,
								rev: line.rev,
								description: line.description,
								quantity: line.quantity,
								// Reset financial fields to 0 per requirements
								cost: 0,
								price: 0,
								// Preserve optional/alternative status
								isAlternative: line.isAlternative,
								isDeleted: false,
								// Copy text lines if any
								textLines: new Collection(
									QuoteTextLine,
									line.textLines?.map(
										(textLine: IQuoteTextLine) =>
											new QuoteTextLine({
												...textLine,
												textLineId: 0, // Reset for new quote
												quoteLineId: 0 // Will be set when line is saved
											})
									) || []
								)
							})
						}) || []
				),

				// Copy document text lines (header text)
				documentTextLines: new Collection(
					QuoteDocumentTextLine,
					sourceQuote.documentTextLines?.map(
						(docLine: IQuoteDocumentTextLine) =>
							new QuoteDocumentTextLine({
								...docLine,
								lineNum: 0 // Reset for new quote
							})
					) || []
				)
			})

			// Set as active record
			activeRecord.value = duplicatedQuote
			activeRecordSavedHash.value = calcHash(duplicatedQuote)

			return duplicatedQuote
		} catch (error) {
			console.error('Failed to duplicate quote:', error)
			activeRecord.value = {} as IQuote
			throw error
		} finally {
			// Ensure storeActive is always set to false when duplication completes
			storeActive.value = false
		}
	}

	function clearQuoteIndicesCache(subjobId?: number) {
		if (subjobId) {
			quoteIndicesBySubjob.value.delete(subjobId)
		} else {
			quoteIndicesBySubjob.value.clear()
		}
	}

	// Search subjobs for standalone quote creation
	async function searchSubJobs(query: string): Promise<ISubJobSearchResult[]> {
		try {
			// Import SubJobService dynamically to avoid circular dependencies
			const { SubJobService } = await import('@/services/API/SubJobService')
			const { useAuthStore } = await import('./authStore')

			const authStoreInstance = useAuthStore()
			const subJobService = new SubJobService(authStoreInstance.authService)
			return await subJobService.searchSubJobs(query)
		} catch (error) {
			console.error('Failed to search subjobs:', error)
			throw error
		}
	}

	// Convenience method for creating a new quote for a subjob
	async function createNewQuoteForSubJob(subjobId: string | number): Promise<IQuote> {
		// Create new quote with subjobId
		await get('0', subjobId)

		// Load related data (subjob, client, reference data)
		await loadRelatedData(subjobId)

		if (!activeRecord.value) {
			throw new Error('Failed to create new quote')
		}

		return activeRecord.value
	}

	// Custom elevated permissions for Quote that checks associated SubJob's salesTeam
	const quoteElevatedPermissions = computed<boolean>(() => {
		// Get authStore inside the computed to ensure reactivity
		const authStore = useAuthStore()

		// Use currentSubJob which gets set when SubJob loads (maintains reactivity)
		if (!currentSubJob.value || !currentSubJob.value.salesTeam) {
			console.log('No currentSubJob or salesTeam for permission check', {
				activeRecord: activeRecord.value,
				currentSubJob: currentSubJob.value
			})
			return false
		}

		const hasElevatedPermission = authStore.mySalesTeams.some(
			(salesPerson) => salesPerson.id === currentSubJob.value?.salesTeam
		)
		console.log('Elevated permission check:', {
			subjobSalesTeam: currentSubJob.value.salesTeam,
			mySalesTeams: authStore.mySalesTeams.map((sp) => sp.id),
			hasElevatedPermission
		})

		return hasElevatedPermission
	})

	// Custom canEdit for Quote that includes elevated permissions logic
	function quoteCanEdit(level: AccessLevel): ComputedRef<boolean> {
		return computed(() => {
			const authStore = useAuthStore()
			if (quoteElevatedPermissions.value) {
				const maxAccessLevel = Math.max(
					AccessLevel.Expanded,
					authStore.getAccessForModuleAndRole('Quote', 'Edit').value
				)
				return level <= maxAccessLevel
			} else {
				return level <= authStore.getAccessForModuleAndRole('Quote', 'Edit').value
			}
		})
	}

	// Custom canRead for Quote that includes elevated permissions logic
	function quoteCanRead(level: AccessLevel): ComputedRef<boolean> {
		return computed(() => {
			const authStore = useAuthStore()
			if (quoteElevatedPermissions.value) {
				const maxAccessLevel = Math.max(
					AccessLevel.Expanded,
					authStore.getAccessForModuleAndRole('Quote', 'Read').value
				)
				return level <= maxAccessLevel
			} else {
				return level <= authStore.getAccessForModuleAndRole('Quote', 'Read').value
			}
		})
	}

	return {
		// All baseStore properties
		id,
		records,
		activeRecord,
		pendingSubjobId,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,

		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		viewList,
		filterList,
		pageSize,
		// Column filters for PrimeTable integration
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		elevatedPermissions: quoteElevatedPermissions,
		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		// BaseStore methods
		canRead: quoteCanRead,
		canEdit: quoteCanEdit,
		canAdd,
		clearActive,
		clearSearch,
		calcHash,

		// Column filter methods
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter,
		getIndex,
		get,
		save,

		// Quote-specific state
		quoteStates,
		quoteStateTransitions,
		pendingApprovalQuotes,
		recentQuotes,
		currentSubJob,

		// Backward compatibility aliases
		currentQuote,
		quotes,
		isLoading,
		errors,

		// Quote-specific computed
		quotesBySubJob,
		openQuotes,
		closedQuotes,

		// Quote-specific methods
		addNew,
		fetchQuote,
		fetchQuotesBySubJob,
		clearQuoteIndicesCache,
		createQuote,
		updateQuote,
		deleteQuote,
		closeQuote,
		cancelQuote,
		transitionQuoteState,
		getQuoteStates,
		getQuoteStateTransitions,
		getQuoteWithRelatedData,
		loadRelatedData,
		searchSubJobs,
		createNewQuoteForSubJob,
		getAvailableTransitionsByState,
		canEditQuote,
		canTransitionQuote,
		calculateQuoteTotal,
		searchQuotes,
		duplicateQuote
	}
})
